// Side-attached Backlog tab — fixed to the right viewport edge. Acts as
// both the toggle for the Backlog rail and a drop target for projects being
// dragged off the Grid. When the user starts dragging a project card, the
// tab thickens and shows a "drop here to backlog" affordance so the
// peeking interaction is discoverable instead of hidden.
//
// Portaled to body so it sits in the same outer stacking context as the
// rail itself — that way IonRouterOutlet's transform doesn't trap either.

import { createPortal } from 'react-dom';

import { IconArchive, IconChev } from './Icons';

interface Props {
  count: number;
  open: boolean;
  onToggle: () => void;
  /** True while a project card is being dragged. Drives the "peek" visual:
   *  the tab widens and gets a dashed drop hint so the user knows they can
   *  release here to backlog the project. */
  dragActive: boolean;
  /** True while a drag is hovering this tab. Highlights the drop zone. */
  dragOverHere: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

const BacklogPeekTab: React.FC<Props> = ({
  count,
  open,
  onToggle,
  dragActive,
  dragOverHere,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  // Wrap in .pm-app so the --rr-*/--pm-* tokens (scoped to that class)
  // resolve when this button mounts directly under <body>.
  return createPortal(
    <div className="pm-app pm-peek-portal">
    <button
      type="button"
      // Tab disappears when the rail is open — its job is done while the
      // user is already inside it. The rail itself has a close X.
      className={[
        'pm-backlog-peek',
        open ? 'pm-backlog-peek-open' : '',
        dragActive ? 'pm-backlog-peek-drag' : '',
        dragOverHere ? 'pm-backlog-peek-over' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={dragActive ? 'Drop here to move project to backlog' : 'Open backlog'}
      aria-expanded={open}
      onClick={onToggle}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      hidden={open}
    >
      <span className="pm-backlog-peek-icon">
        <IconArchive size={14} />
      </span>
      <span className="pm-backlog-peek-label">
        {dragActive ? 'Drop to backlog' : 'Backlog'}
      </span>
      <span className="pm-backlog-peek-count">{count}</span>
      <span className="pm-backlog-peek-chev" aria-hidden="true">
        <IconChev size={12} />
      </span>
    </button>
    </div>,
    document.body,
  );
};

export default BacklogPeekTab;
